import React, { useState, useRef, useEffect } from "react";
import moment from "moment";
import { useNavigate } from "react-router-dom"; 
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import busStations from "../../../Data/BusStations.json";
import {
  RiMapPinLine,
  RiMapPinFill,
  RiSearchLine,
  RiSwapLine,
  RiCalendarTodoLine,
  RiTimerLine,
  RiShieldCheckLine
} from "react-icons/ri";
import styles from "./SearchBusPanel4.module.css";

const CustomDateInput = React.forwardRef(({ value, onClick }, ref) => (
  <div onClick={onClick} ref={ref} className={styles.dateInput}>
    <RiCalendarTodoLine className={styles.dateIcon} />
    <span className={`${styles.dateText} ${value ? styles.dateSelected : ''}`}>
      {value || 'Select Date'}
    </span>
  </div>
));


const SearchBusPanel4 = ({ onSearch, mode, onNavigate, templateId }) => {
   const navigate = useNavigate();
  const [fromStation, setFromStation] = useState("HYDERABAD");
  const [toStation, setToStation] = useState("BANGALORE");
  const [fromStationId, setFromStationId] = useState("");
  const [toStationId, setToStationId] = useState("");
  const [travelDate, setTravelDate] = useState(new Date());
  const [errors, setErrors] = useState({});
  const [activeField, setActiveField] = useState(null);
  const [fromStationResults, setFromStationResults] = useState([]);
  const [toStationResults, setToStationResults] = useState([]);
  
  const fromInputRef = useRef(null);
  const toInputRef = useRef(null);

  const handleSwapStations = () => {
    setFromStation(toStation);
    setToStation(fromStation);
    setFromStationId(toStationId);
    setToStationId(fromStationId);
  };

  const handleFromStationSearch = (e) => {
    const value = e.target.value;
    setFromStation(value);
    if (value.length > 0) {
      const filteredStations = busStations.stationList
        .filter((station) =>
          station.stationName.toLowerCase().includes(value.toLowerCase())
        )
        .slice(0, 8);
      setFromStationResults(filteredStations);
    } else {
      setFromStationResults([]);
    }
  };

  const handleToStationSearch = (e) => {
    const value = e.target.value;
    setToStation(value);
    if (value.length > 0) {
      const filteredStations = busStations.stationList
        .filter((station) =>
          station.stationName.toLowerCase().includes(value.toLowerCase())
        )
        .slice(0, 8);
      setToStationResults(filteredStations);
    } else {
      setToStationResults([]);
    }
  };

  const handleFromStationSelect = (station) => {
    setFromStation(station.stationName);
    setFromStationId(station.stationId);
    setFromStationResults([]);
    setActiveField(null);
  };

  const handleToStationSelect = (station) => {
    setToStation(station.stationName);
    setToStationId(station.stationId);
    setToStationResults([]);
    setActiveField(null);
  };

  const validate = () => {
    let temp = {};
    temp.fromStation = fromStation !== "";
    temp.toStation = toStation !== "";
    temp.travelDate = travelDate !== null;
    temp.sameStation = fromStation !== toStation;
    
    setErrors(temp);
    return Object.values(temp).every(x => x === true);
  };

  const handleSearch = (e) => {
    e.preventDefault();
    if (validate()) {
      const searchQuery = {
        fromStationCode: fromStationId,
        fromStation: fromStation,
        toStationCode: toStationId,
        toStation: toStation,
        travelDate: moment(travelDate).format("YYYY-MM-DD"),
      };
      if (mode === "builder") {
        onNavigate({ name: "bus-results", params: { searchQuery } });
      } else if (mode === "preview") {
        navigate(`/template-preview/${templateId}/bus-results`, { state: { searchQuery } });
      } else {
        onSearch(searchQuery); // For live site
      }
    }
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest(`.${styles.resultsList}`)) {
        setFromStationResults([]);
        setToStationResults([]);
        setActiveField(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div className={styles.container}>
      {/* Main Search Bar */}
      <div className={styles.searchBar}>
        {/* From */}
        <div className={styles.inputGroup}>
          <div className={styles.inputWrapper}>
            <RiMapPinLine className={styles.inputIcon} />
            <input
              ref={fromInputRef}
              type="text"
              placeholder="From where?"
              value={fromStation}
              onChange={handleFromStationSearch}
              onFocus={() => {
                setActiveField('from');
                setFromStationResults(busStations.stationList.slice(0, 8));
              }}
              className={`${styles.input} ${errors.fromStation === false ? styles.inputError : ''}`}
            />
          </div>
          
          {fromStationResults.length > 0 && activeField === 'from' && (
            <ul className={styles.resultsList}>
              {fromStationResults.map((station) => (
                <li
                  key={station.stationId}
                  onClick={() => handleFromStationSelect(station)}
                  className={styles.resultItem}
                >
                  <RiMapPinLine className={styles.resultIcon} />
                  <span>{station.stationName}</span>
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* Swap Button */}
        <button
          onClick={handleSwapStations}
          className={styles.swapBtn}
        >
          <RiSwapLine />
        </button>

        {/* To */}
        <div className={styles.inputGroup}>
          <div className={styles.inputWrapper}>
            <RiMapPinFill className={styles.inputIcon} />
            <input
              ref={toInputRef}
              type="text"
              placeholder="To where?"
              value={toStation}
              onChange={handleToStationSearch}
              onFocus={() => {
                setActiveField('to');
                setToStationResults(busStations.stationList.slice(0, 8));
              }}
              className={`${styles.input} ${errors.toStation === false ? styles.inputError : ''}`}
            />
          </div>
          
          {toStationResults.length > 0 && activeField === 'to' && (
            <ul className={styles.resultsList}>
              {toStationResults.map((station) => (
                <li
                  key={station.stationId}
                  onClick={() => handleToStationSelect(station)}
                  className={styles.resultItem}
                >
                  <RiMapPinFill className={styles.resultIcon} />
                  <span>{station.stationName}</span>
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* Date */}
        <div className={styles.dateGroup}>
          <DatePicker
            selected={travelDate}
            onChange={setTravelDate}
            customInput={<CustomDateInput />}
            dateFormat="dd MMM yyyy"
            minDate={new Date()}
          />
        </div>

        {/* Search Button */}
        <button
          onClick={handleSearch}
          className={styles.searchBtn}
        >
          <RiSearchLine className={styles.searchIcon} />
          <span>Search</span>
        </button>
      </div>

      {/* Quick Info Bar */}
      <div className={styles.infoBar}>
        <div className={styles.infoItem}>
          <RiTimerLine className={styles.infoIcon} />
          <span>500+ Daily Buses</span>
        </div>
        <div className={styles.infoDivider}></div>
        <div className={styles.infoItem}>
          <RiShieldCheckLine className={styles.infoIcon} />
          <span>Safe & Sanitized</span>
        </div>
      </div>

      {/* Error Messages */}
      {Object.values(errors).some(v => v === false) && (
        <div className={styles.errorBar}>
          {errors.fromStation === false && <span>Select departure</span>}
          {errors.toStation === false && <span>Select destination</span>}
          {errors.travelDate === false && <span>Select date</span>}
          {errors.sameStation === false && <span>Different cities</span>}
        </div>
      )}
    </div>
  );
};

export default SearchBusPanel4;